import type { IngatlanTipus } from '@ertekbecslo/becslo';
import { JELLEMZOK } from '@ertekbecslo/becslo/jellemzok';
import type { VarazsloAllapot } from './allapot.ts';
import { EPULET_CIMKE, ERTEK_CIMKE, JELLEMZO_KERDES } from './cimkek.ts';

export interface Opcio { ertek: string; cimke: string }
/** `hova`: a `jellemzo` a becslést befolyásolhatja, az `extra` csak a visszahívó kollégának szól (kutatás 11. pont). */
export interface ValasztoKerdes {
  fajta: 'valaszto'; kulcs: string; kerdes: string; alcim?: string; opciok: Opcio[];
  kotelezo: boolean; hova: 'jellemzo' | 'epuletTipus' | 'extra';
}
/** Több válasz egyszerre; az értéke az `extra`-ban az opciók sorrendjében, vesszővel összefűzve. */
export interface TobbValaszto {
  fajta: 'tobb'; kulcs: string; kerdes: string; alcim?: string; opciok: Opcio[]; kotelezo: boolean; hova: 'extra';
}
export interface SzamKerdes {
  fajta: 'szam'; kulcs: string; kerdes: string; mertekegyseg?: string;
  min: number; max: number; egesz: boolean; kotelezo: boolean; hova: 'alapterulet' | 'extra';
}
export type Kerdes = ValasztoKerdes | TobbValaszto | SzamKerdes;

const ALAPTERULET_KERDES: Record<IngatlanTipus, string> = {
  lakas: 'Mekkora a lakás alapterülete?', haz: 'Mekkora a ház lakótere?', iroda: 'Mekkora az iroda alapterülete?',
  ipari: 'Mekkora az ingatlan alapterülete?', telek: 'Mekkora a telek?', garazs: 'Mekkora a garázs?',
};

const opciok = (...p: [string, string][]): Opcio[] => p.map(([ertek, cimke]) => ({ ertek, cimke }));

const ENERGETIKA_ALCIM = 'Az energetikai tanúsítványon találja. AA–BB: korszerű, CC–EE: átlagos, FF–JJ: korszerűtlen.';

/** A becslést befolyásoló kérdés: az opciók a `JELLEMZOK` értékei. */
function jellemzo(tipus: IngatlanTipus, kulcs: string, kotelezo: boolean, alcim?: string): ValasztoKerdes {
  const ertekek = JELLEMZOK[tipus][kulcs];
  if (!ertekek) throw new Error(`ismeretlen jellemző: ${tipus}.${kulcs}`);
  return {
    fajta: 'valaszto', kulcs, kerdes: JELLEMZO_KERDES[kulcs] ?? kulcs, alcim, hova: 'jellemzo', kotelezo,
    opciok: ertekek.map((e) => ({ ertek: e, cimke: ERTEK_CIMKE[`${kulcs}.${e}`] ?? e })),
  };
}
const extraValaszto = (kulcs: string, kerdes: string, o: Opcio[], kotelezo: boolean): ValasztoKerdes =>
  ({ fajta: 'valaszto', kulcs, kerdes, opciok: o, kotelezo, hova: 'extra' });
const extraTobb = (kulcs: string, kerdes: string, o: Opcio[], kotelezo: boolean): TobbValaszto =>
  ({ fajta: 'tobb', kulcs, kerdes, opciok: o, kotelezo, hova: 'extra' });
const extraSzam = (kulcs: string, kerdes: string, kotelezo: boolean, min: number, max: number): SzamKerdes =>
  ({ fajta: 'szam', kulcs, kerdes, mertekegyseg: 'm²', min, max, egesz: true, kotelezo, hova: 'extra' });

const KOZMUVEK = opciok(['viz', 'Víz'], ['villany', 'Villany'], ['gaz', 'Gáz'], ['csatorna', 'Csatorna']);
const HASZNOSITAS = opciok(['sajat', 'Saját használat'], ['berbe_adva', 'Bérbe adva'], ['ures', 'Üres']);
const IGEN_NEM = opciok(['igen', 'Igen'], ['nem', 'Nem']);

/**
 * Kérdéssor típusonként, a megjelenés sorrendjében. Kötelező: a becsléshez vagy a visszahíváshoz nélkülözhetetlen;
 * opcionális: a látogató átugorhatja. Az `extra` kérdések nem módosítják a becslést (kivéve a szakértői ágat,
 * lásd `SZAKERTOI_JELLEMZOK`), a lead `extra` mezőjébe mennek.
 */
function tipusKerdesei(tipus: IngatlanTipus): Kerdes[] {
  switch (tipus) {
    case 'lakas':
      return [
        jellemzo(tipus, 'allapot', true), jellemzo(tipus, 'emelet', true), jellemzo(tipus, 'erkely', true),
        extraValaszto('szobak', 'Hány szobás?', opciok(['1', '1 szoba'], ['2', '2 szoba'], ['3', '3 szoba'], ['4_plusz', '4 vagy több szoba']), true),
        jellemzo(tipus, 'parkolas', false), jellemzo(tipus, 'energetika', false, ENERGETIKA_ALCIM),
      ];
    case 'haz':
      return [
        extraSzam('telekmeret', 'Mekkora a telek?', true, 50, 100_000),
        extraValaszto('epitesEve', 'Melyik időszakban épült?', opciok(
          ['1945_elott', '1945 előtt'], ['1945_1980', '1945–1980'], ['1981_2000', '1981–2000'], ['2001_2015', '2001–2015'], ['2016_utan', '2016 után'],
        ), true),
        jellemzo(tipus, 'allapot', true), jellemzo(tipus, 'falazat', true),
        extraValaszto('futes', 'Mivel fűtenek?', opciok(
          ['gaz', 'Gáz'], ['tavfutes', 'Távfűtés'], ['hoszivattyu', 'Hőszivattyú'], ['fa_vegyes', 'Fa vagy vegyes'], ['elektromos', 'Elektromos'],
        ), false),
        extraTobb('kozmuvek', 'Milyen közművek vannak?', KOZMUVEK, false),
        jellemzo(tipus, 'energetika', false, ENERGETIKA_ALCIM), jellemzo(tipus, 'parkolas', false),
      ];
    case 'telek':
      return [
        jellemzo(tipus, 'belterulet', true), jellemzo(tipus, 'kozmu', true),
        extraValaszto('megkozelites', 'Milyen úton közelíthető meg?', opciok(
          ['aszfaltos', 'Aszfaltos út'], ['szilard', 'Szilárd burkolatú út'], ['foldut', 'Földút'], ['maganut', 'Magánút'],
        ), false),
        extraValaszto('epuletATelken', 'Áll-e épület a telken?', opciok(['nincs', 'Nincs'], ['bontando', 'Bontandó'], ['hasznalhato', 'Használható']), false),
      ];
    case 'iroda':
      return [
        jellemzo(tipus, 'kategoria', true, 'A: modern irodaház. B: régebbi irodaház. C: régi vagy lakásból átalakított iroda.'),
        jellemzo(tipus, 'allapot', true),
        extraValaszto('hasznositas', 'Hogyan hasznosítják most?', HASZNOSITAS, true),
        extraValaszto('parkolohelyek', 'Hány parkolóhely tartozik hozzá?', opciok(['0', 'Egy sem'], ['1_2', '1–2'], ['3_plusz', '3 vagy több']), false),
        extraValaszto('szintLift', 'Hol található az épületben?', opciok(
          ['foldszint', 'Földszint'], ['emelet_lifttel', 'Emelet, lifttel'], ['emelet_lift_nelkul', 'Emelet, lift nélkül'],
        ), false),
      ];
    case 'ipari':
      return [
        extraSzam('telekterulet', 'Mekkora a hozzá tartozó telek?', false, 50, 1_000_000),
        jellemzo(tipus, 'kategoria', true), jellemzo(tipus, 'allapot', true),
        extraValaszto('belmagassag', 'Mekkora a belmagasság?', opciok(['4_alatt', '4 m alatt'], ['4_6', '4–6 m'], ['6_9', '6–9 m'], ['9_felett', '9 m felett']), false),
        extraValaszto('rakodas', 'Hogyan lehet rakodni?', opciok(['rampa', 'Rámpa'], ['szintbeli_kapu', 'Szintbeli kapu'], ['nincs', 'Egyik sem']), false),
        extraValaszto('hasznositas', 'Hogyan hasznosítják most?', HASZNOSITAS, false),
      ];
    case 'garazs':
      return [
        jellemzo(tipus, 'fajta', true),
        extraValaszto('nyilvantartas', 'Hogyan szerepel az ingatlan-nyilvántartásban?', opciok(
          ['onallo_hrsz', 'Önálló helyrajzi szám'], ['albetet', 'Albetét'], ['nem_tudom', 'Nem tudom'],
        ), false),
        extraValaszto('zarhato', 'Zárható?', IGEN_NEM, false),
        extraValaszto('villanyToltes', 'Van villany, és lehet-e autót tölteni?', IGEN_NEM, false),
      ];
  }
}

export function kerdesekTipushoz(tipus: IngatlanTipus): Kerdes[] {
  const alapterulet: SzamKerdes = {
    fajta: 'szam', kulcs: 'alapterulet', kerdes: ALAPTERULET_KERDES[tipus], mertekegyseg: 'm²',
    min: 1, max: 100_000, egesz: false, kotelezo: true, hova: 'alapterulet',
  };
  const epulet: Kerdes[] = tipus === 'lakas'
    ? [{ fajta: 'valaszto', kulcs: 'epuletTipus', kerdes: 'Milyen épületben van?', hova: 'epuletTipus', kotelezo: true,
        opciok: (['tegla', 'panel', 'uj_epitesu'] as const).map((e) => ({ ertek: e, cimke: EPULET_CIMKE[e] })) }]
    : [];
  const sor = [...epulet, ...tipusKerdesei(tipus)];
  // Az alapterület általában az első kérdés, lakásnál az épülettípus, garázsnál a fajta után jön.
  sor.splice(epulet.length + (tipus === 'garazs' ? 1 : 0), 0, alapterulet);
  return sor;
}

type Valaszok = Pick<VarazsloAllapot, 'epuletTipus' | 'jellemzok' | 'extra'>;

/** A tobbvalasztos kérdés mentett szövegéből az érvényes értékek (az opciók sorrendjében). */
export function tobbErtekek(k: TobbValaszto, nyers: string): string[] {
  const kivalasztott = new Set(nyers.split(',').map((s) => s.trim()));
  return k.opciok.filter((o) => kivalasztott.has(o.ertek)).map((o) => o.ertek);
}

/** Egy jelölőnégyzet átváltása; az eredmény a mentett szöveg (`extra[k.kulcs]`). */
export function tobbValtas(k: TobbValaszto, nyers: string, ertek: string, bekapcsolva: boolean): string {
  const mar = new Set(tobbErtekek(k, nyers));
  if (bekapcsolva) mar.add(ertek); else mar.delete(ertek);
  return k.opciok.filter((o) => mar.has(o.ertek)).map((o) => o.ertek).join(',');
}

/**
 * A látogató érvényes válasza (egy érték, ill. a több válasz vesszős szövege), vagy `null`, ha üres vagy az opciók
 * között nem szereplő (pl. másik típus régi mentéséből maradt) érték. Az érvényesség ellenőrzése és a beküldés
 * ugyanezt használja, így a kettő nem térhet el.
 */
export function valasz(k: ValasztoKerdes | TobbValaszto, a: Valaszok): string | null {
  const nyers = k.hova === 'epuletTipus' ? (a.epuletTipus ?? '') : k.hova === 'jellemzo' ? (a.jellemzok[k.kulcs] ?? '') : (a.extra[k.kulcs] ?? '');
  if (k.fajta === 'tobb') {
    const e = tobbErtekek(k, nyers);
    return e.length ? e.join(',') : null;
  }
  return k.opciok.some((o) => o.ertek === nyers) ? nyers : null;
}

/** Az `extra` mezőbe kerülő, a kollégának olvasható szöveg (az opciók címkéi, vesszővel elválasztva). */
export function extraSzoveg(k: ValasztoKerdes | TobbValaszto, ertek: string): string {
  const e = new Set(ertek.split(','));
  return k.opciok.filter((o) => e.has(o.ertek)).map((o) => o.cimke).join(', ');
}
